import { MainContent } from '../shared-mui-components/MainContent.tsx';
import { HeaderWithButtons } from '../shared-mui-components/HeaderWithButtons.tsx';
import {
    Localized,
    useLanguage,
    useTranslation,
} from 'dg-web-shared/lib/Localization.tsx';
import { BarChart, CloudDownload } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import {
    ChartSection,
    DataPointUnit,
    numberFormatter,
    OperatorBarChart,
    ScrollableContainer,
} from './OperatorBarChart.tsx';
import { FilterBar } from '../shared-mui-components/filter/FiltersBox.tsx';
import { useCheckinStatisticsFilterConfiguration } from '../shared-mui-components/filter/OperatorFilterConfiguration.tsx';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import { FiltersDropdown } from '../parkingabo-users/OperatorParkingaboUsersFilterList.tsx';
import {
    buildFilterPayload,
    setSearchURL,
} from '../shared-mui-components/filter/OperatorFilterHelpers.tsx';
import { useLocation, useNavigate } from 'react-router';
import {
    ButtonWithDropdown,
    DropdownItem,
} from '../layout/components/ButtonWithDropdown.tsx';
import { Fragment, useState } from 'react';
import {
    RequestStatus,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { openNewTab } from 'dg-web-shared/common/utils/Navigation.tsx';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import { EmptyResults } from '../shared-mui-components/ListMessages.tsx';
import { useReadOnlyExportContext } from '../app/components/ReadOnlyExportContext.tsx';
import {
    ConfirmationDialog,
    ConfirmationDialogVersions,
} from '../operator-logins/components/ConfirmationDialog.tsx';
import Alert from '@mui/material/Alert';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';
import { urlWithOperatorExternalId } from '../utils/Utils.ts';

export interface CheckinStatisticsResponse {
    data: CheckinStatisticYear[];
    maxMonthTotalRevenue: number;
    maxMonthTotalCount: number;
    aboveSingleTransactionExportLimit: boolean;
}

interface CheckinStatisticYear {
    year: number;
    totalRevenue: number;
    totalCount: number;
    months: CheckinStatisticMonth[];
}

enum AggregateDuration {
    MONTHLY = 'MONTHLY',
    DAILY = 'DAILY',
}

interface CheckinStatisticMonth {
    yearMonth: string;
    countTotal: number;
    revenueTotal: number;
    countParkingpay: number;
    revenueParkingpay: number;
    countTwint: number;
    revenueTwint: number;
    countEasypark: number;
    revenueEasypark: number;
    countCoop: number;
    revenueCoop: number;
    countTcs: number;
    revenueTcs: number;
    countSwisspass: number;
    revenueSwisspass: number;
    countPnR: number;
    revenuePnR: number;
    countParkingabo: number;
    revenueParkingabo: number;
    countParkingaboCounter: number;
    revenueParkingaboCounter: number;
    countQuickcheckout: number;
    revenueQuickcheckout: number;
    countTom: number;
    revenueTom: number;
    countPayStation: number;
    revenuePayStation: number;
    countPayStationCash: number;
    revenuePayStationCash: number;
    countPayStationCashless: number;
    revenuePayStationCashless: number;
}

export function OperatorCheckinStatisticsRoute() {
    const { operatorExternalId } = useOperatorContext();
    const {
        filterConfig,
        activeFilters,
        listState,
        filterConfigState,
        searchText,
    } = useCheckinStatisticsFilterConfiguration();
    const { language } = useLanguage();
    const navigate = useNavigate();
    const location = useLocation();
    const readOnlyTransactions = useReadOnlyExportContext();
    const [singleTransagtionModalOpen, setSingleTransagtionModalOpen] =
        useState(false);

    const [transactionExportState, requestTransactionExport] = useServerWrite<
        string,
        null
    >(queryString => ({
        url: `/ui-api/operator-account/statistics/check-ins/export/single-transactions?${queryString}&language=${language}`,
    }));

    useServerSuccessEffect(
        transactionExportState,
        readOnlyTransactions.refetchReadOnlyExportData,
    );

    const payStation = useTranslation({
        de: 'Kasse',
        fr: 'Caisse',
        it: 'Cassa',
        en: 'Pay station',
    });
    const payStationCash = useTranslation({
        de: 'Kasse (Bargeld)',
        fr: 'Caisse (espèces)',
        it: 'Cassa (contanti)',
        en: 'Pay station (cash)',
    });
    const payStationCashless = useTranslation({
        de: 'Kasse (bargeldlos)',
        fr: 'Caisse (sans espèces)',
        it: 'Cassa (senza contanti)',
        en: 'Pay station (cashless)',
    });
    const parkingaboCounter = useTranslation({
        de: 'Parkingabo (Schalter)',
        fr: 'Parkingabo (guichet)',
        it: 'Parkingabo (sportello)',
        en: 'Parkingabo (counter)',
    });
    const parkingaboEcommerce = useTranslation({
        de: 'Parkingabo (E-Commerce)',
        fr: 'Parkingabo (e-commerce)',
        it: 'Parkingabo (e-commerce)',
        en: 'Parkingabo (e-commerce)',
    });
    const tooltipNames = [
        'TWINT',
        'Parkingpay',
        'EasyPark',
        'Coop',
        'TCS',
        'SwissPass',
        'P+Rail',
        parkingaboEcommerce,
        parkingaboCounter,
        'Quick-Checkout',
        'TOMeco',
        payStation,
        payStationCash,
        payStationCashless,
    ];

    const hasRunningExport = readOnlyTransactions.runningExportId !== null;
    const notReady = listState.status !== RequestStatus.SUCCESS;
    const aboveSingleTransactionExportLimit =
        listState.status === RequestStatus.SUCCESS &&
        listState.data.aboveSingleTransactionExportLimit;
    const exportDropdownItems: DropdownItem[] = [
        {
            key: 'group_day',
            label: (
                <Localized
                    de="Gruppiert nach Tag"
                    fr="Groupée par jour"
                    it="Raggruppata per giorno"
                    en="Grouped by day"
                />
            ),
            disabled: notReady,
            onClickCallback: () => {
                openNewTab(
                    urlWithOperatorExternalId(
                        `/ui-api/operator-account/statistics/check-ins/export/xlsx?lang=${language}&aggregateDuration=${
                            AggregateDuration.DAILY
                        }&${buildFilterPayload(activeFilters, searchText)}`,
                        operatorExternalId,
                    ),
                );
            },
            tooltip: notReady ? <StatisticNotReadyTooltip /> : undefined,
        },
        {
            key: 'group_month',
            label: (
                <Localized
                    de="Gruppiert nach Monat"
                    fr="Groupée par mois"
                    it="Raggruppata per mese"
                    en="Grouped by month"
                />
            ),
            disabled: notReady,
            onClickCallback: () => {
                openNewTab(
                    urlWithOperatorExternalId(
                        `/ui-api/operator-account/statistics/check-ins/export/xlsx?lang=${language}&aggregateDuration=${
                            AggregateDuration.MONTHLY
                        }&${buildFilterPayload(activeFilters, searchText)}`,
                        operatorExternalId,
                    ),
                );
            },
            tooltip: notReady ? <StatisticNotReadyTooltip /> : undefined,
        },
        {
            key: 'single_transactions',
            label: (
                <Localized
                    de="Einzeln"
                    fr="Individuellement"
                    it="Singolarmente"
                    en="Individually"
                />
            ),
            tooltip: hasRunningExport ? (
                <Localized
                    de="Ein export läuft bereits."
                    fr="Une exportation est déjà en cours."
                    it="È già in corso un'esportazione."
                    en="An export is already running."
                />
            ) : notReady ? (
                <StatisticNotReadyTooltip />
            ) : aboveSingleTransactionExportLimit ? (
                <Localized
                    de="Der Export hat ein Maximum von einer Million, schränken Sie die Statistik weiter ein."
                    fr="L'exportation a un maximum d'un million, veuillez restreindre davantage les statistiques."
                    it="L'esportazione ha un massimo di un milione, restringi ulteriormente le statistiche."
                    en="The export has a maximum of one million, please further narrow the statistics."
                />
            ) : undefined,
            disabled:
                transactionExportState.status === RequestStatus.PENDING ||
                hasRunningExport ||
                notReady ||
                aboveSingleTransactionExportLimit,
            onClickCallback: () => setSingleTransagtionModalOpen(true),
        },
    ];

    return (
        <MainContent>
            <HeaderWithButtons
                title={
                    <Localized
                        de={`Statistiken ${EmDash} Parkvorgänge`}
                        fr={`Statistiques ${EmDash} Procédures`}
                        it={`Statistiche ${EmDash} Procedure`}
                        en={`Statistics ${EmDash} Procedures`}
                    />
                }
                icon={BarChart}
                headerButtons={
                    <ButtonWithDropdown
                        label={
                            <Localized
                                de="Exportieren"
                                fr="Exporter"
                                it="Esporta"
                                en="Export"
                            />
                        }
                        itemVariant={'action'}
                        variant="outlined"
                        startIcon={<CloudDownload />}
                        dropdownItems={exportDropdownItems}
                    />
                }
            />
            <FilterBar>
                <FiltersDropdown
                    activeFilters={activeFilters}
                    filterConfig={filterConfig}
                    onFiltersStateChange={activeFilters =>
                        setSearchURL(
                            navigate,
                            location,
                            searchText,
                            activeFilters,
                        )
                    }
                    pending={
                        filterConfigState.status === RequestStatus.PENDING &&
                        Object.keys(filterConfig).length === 0
                    }
                />
            </FilterBar>
            <ScrollableContainer>
                <Alert severity="info" sx={{ mx: 4 }}>
                    <Localized
                        de="Die Werte der Statistiken werden anhand des Enddatums des Parkvorgangs berechnet."
                        fr="Les valeurs des statistiques sont calculées sur la base de la date de fin du stationnement."
                        it="I valori delle statistiche vengono calcolati sulla base della data di fine della procedura."
                        en="The values in the statistics are calculated based on the end date of the parking transaction."
                    />
                </Alert>
                <OperatorAsyncLoadedSection
                    requestState={listState}
                    render={data => {
                        if (data.data.length === 0) {
                            return (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        height: '100%',
                                    }}
                                >
                                    <EmptyResults />
                                </Box>
                            );
                        }

                        return (
                            <>
                                {data.data.map(yearData => (
                                    <Fragment key={yearData.year}>
                                        <Typography
                                            component="div"
                                            variant="h6"
                                            sx={{
                                                backgroundColor: theme =>
                                                    theme.palette.primary.main,
                                                color: theme =>
                                                    theme.palette.primary
                                                        .contrastText,
                                                fontWeight: theme =>
                                                    theme.typography
                                                        .fontWeightBold,
                                                textAlign: 'center',
                                                marginX: 4,
                                                marginTop: 2,
                                            }}
                                        >
                                            {yearData.year}
                                        </Typography>
                                        <Stack
                                            direction="row"
                                            spacing={4}
                                            sx={{
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                                margin: 4,
                                                marginTop: 2,
                                                marginBottom: 8,
                                            }}
                                        >
                                            <ChartSection
                                                title={
                                                    <>
                                                        <Localized
                                                            de="Anzahl "
                                                            fr="Nombre "
                                                            it="Numero "
                                                            en="Number "
                                                        />
                                                    </>
                                                }
                                                caption={
                                                    <>
                                                        (
                                                        {numberFormatter(
                                                            yearData.totalCount,
                                                            DataPointUnit.PCS,
                                                            false,
                                                        )}
                                                        )
                                                    </>
                                                }
                                            >
                                                <OperatorBarChart
                                                    data={yearData.months}
                                                    maxYValue={
                                                        data.maxMonthTotalCount
                                                    }
                                                    totalKey="countTotal"
                                                    dataKeys={[
                                                        'countTwint',
                                                        'countParkingpay',
                                                        'countEasypark',
                                                        'countCoop',
                                                        'countTcs',
                                                        'countSwisspass',
                                                        'countPnR',
                                                        'countParkingabo',
                                                        'countParkingaboCounter',
                                                        'countQuickcheckout',
                                                        'countTom',
                                                        'countPayStation',
                                                        'countPayStationCash',
                                                        'countPayStationCashless',
                                                    ]}
                                                    tooltipNames={tooltipNames}
                                                    yearMonthKey="yearMonth"
                                                />
                                            </ChartSection>
                                            <ChartSection
                                                title={
                                                    <>
                                                        <Localized
                                                            de="Volumen "
                                                            fr="Volume "
                                                            it="Volume "
                                                            en="Volume "
                                                        />
                                                    </>
                                                }
                                                caption={
                                                    <>
                                                        (
                                                        {numberFormatter(
                                                            yearData.totalRevenue,
                                                            DataPointUnit.CHF,
                                                            true,
                                                        )}
                                                        )
                                                    </>
                                                }
                                            >
                                                <OperatorBarChart
                                                    data={yearData.months}
                                                    maxYValue={
                                                        data.maxMonthTotalRevenue
                                                    }
                                                    totalKey="revenueTotal"
                                                    dataKeys={[
                                                        'revenueTwint',
                                                        'revenueParkingpay',
                                                        'revenueEasypark',
                                                        'revenueCoop',
                                                        'revenueTcs',
                                                        'revenueSwisspass',
                                                        'revenuePnR',
                                                        'revenueParkingabo',
                                                        'revenueParkingaboCounter',
                                                        'revenueQuickcheckout',
                                                        'revenueTom',
                                                        'revenuePayStation',
                                                        'revenuePayStationCash',
                                                        'revenuePayStationCashless',
                                                    ]}
                                                    tooltipNames={tooltipNames}
                                                    yearMonthKey="yearMonth"
                                                    unit={DataPointUnit.CHF}
                                                />
                                            </ChartSection>
                                        </Stack>
                                    </Fragment>
                                ))}
                            </>
                        );
                    }}
                />
            </ScrollableContainer>
            <ConfirmReadOnlyExport
                open={singleTransagtionModalOpen}
                onConfirm={() => {
                    requestTransactionExport(
                        buildFilterPayload(activeFilters, searchText),
                    );
                    setSingleTransagtionModalOpen(false);
                }}
                onCancel={() => setSingleTransagtionModalOpen(false)}
            />
        </MainContent>
    );
}

export function ConfirmReadOnlyExport({
    open,
    onConfirm,
    onCancel,
}: {
    open: boolean;
    onConfirm: () => void;
    onCancel?: () => void;
}) {
    return (
        <ConfirmationDialog
            open={open}
            title={
                <Localized
                    de="Einzeln exportieren"
                    fr="Exporter individuellement"
                    it="Export singolarmente"
                    en="Export individually"
                />
            }
            onCancel={onCancel}
            cancelLabel={
                <Localized
                    de="Abbrechen"
                    fr="Annuler"
                    it="Annulla"
                    en="Cancel"
                />
            }
            onConfirm={onConfirm}
            confirmLabel={
                <Localized
                    de="Weiter"
                    fr="Continuer"
                    it="Continua"
                    en="Continue"
                />
            }
            dialogVersion={ConfirmationDialogVersions.PRIMARY}
        >
            <>
                <p>
                    <Localized
                        de="Die Datei mit den angeforderten Daten wird im Hintergrund erstellt. Sobald der Vorgang abgeschlossen ist, erhalten Sie eine Benachrichtigungs-E-Mail mit dem Link zum Download."
                        fr="Le fichier contenant les données demandées sera généré en arrière-plan; dès que l'opération sera terminée, vous recevrez un e-mail de notification contenant le lien pour le téléchargement."
                        it="Il file contente i dati richiesti verrà generato in background; appena completata l'operazione riceverà un messaggio e-mail di notifica contenente il link per il download."
                        en="The file containing the requested data will be generated in the background; as soon as the operation is complete, you will receive a notification e-mail containing the download link."
                    />
                </p>
                <p>
                    <Localized
                        de="Der Verlauf der von Ihnen angeforderten Dateien ist 7 Tage lang im Benutzermenü oben rechts unter «Downloads» verfügbar."
                        fr="L'historique des fichiers que vous avez demandés est disponible pendant 7 jours dans le menu utilisateur en haut à droite, sous la rubrique «Téléchargements»."
                        it="Lo storico dei file da lei richiesti è disponibile per 7 giorni nel menu utente in alto a destra, alla voce «Downloads»."
                        en="The history of the files you have requested is available for 7 days in the user menu at the top right, under “Downloads”."
                    />
                </p>
            </>
        </ConfirmationDialog>
    );
}

export function StatisticNotReadyTooltip() {
    return (
        <Localized
            de="Die Statistik ist noch nicht bereit."
            fr="Les statistiques ne sont pas encore prêtes."
            it="Le statistiche non sono ancora pronte."
            en="The statistics are not ready yet."
        />
    );
}
